import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, Alert } from '@mui/material';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Bookings from './pages/Bookings';
import Users from './pages/Users';
import Events from './pages/Events';
import EventConfig from './pages/EventConfig';
import Analytics from './pages/Analytics';
import BlockedDates from './pages/BlockedDates';
import PromoCodes from './pages/PromoCodes';
import PaymentSettings from './pages/PaymentSettings';
import DashboardLayout from './components/DashboardLayout';

// Protected Route Component for role-based access
const ProtectedRoute = ({ children, allowedRoles }) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Sie haben keine Berechtigung, diese Seite anzuzeigen.</Alert>
      </Box>
    );
  }
  
  return children;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      setIsAuthenticated(true);
      setUser(JSON.parse(userData));
    }
    
    setLoading(false);
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Router>
      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Login onLogin={handleLogin} />
            )
          }
        />
        
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <DashboardLayout user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="bookings" element={<Bookings user={user} />} />
          <Route 
            path="analytics" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <Analytics />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="events" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <Events />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="events/:eventId/config" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <EventConfig />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="promo-codes" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <PromoCodes />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="blocked-dates" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <BlockedDates />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="users" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Users user={user} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="payment-settings" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <PaymentSettings />
              </ProtectedRoute>
            } 
          />
        </Route>

        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
